import styled from '@emotion/styled'
import { Button } from '@mui/material'
import { useAtomValue } from 'jotai'
import { memo } from 'react'
import { v3WithdrawalBalanceAtom } from './atoms'
import { useCurrentNetwork } from 'store/app/hooks'
import { useSwitchNetwork } from 'hooks/web3/useSwitchNetwork'
import { useWeb3React } from '@web3-react/core'
import { Interface, formatUnits, parseUnits } from 'ethers/lib/utils'
import withdrawalAbi from './abi.json'
import { V3WithdrawalZkLinkChains } from './constants'
import toastify from 'components/Toastify'
import { BigNumber } from 'ethers'
import { toFixed } from 'utils/number'

const Wrap = styled('div')`
  display: flex;
  gap: 4px;
  align-items: center;
  height: 52px;
  font-size: 14px;
  color: rgba(255, 255, 255, 1);
  padding: 0 4px;
`
const TokenIcon = styled('img')`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`
const TokenSymbol = styled('div')`
  width: 80px;
  font-weight: 600;
`
const TokenAvailable = styled('div')`
  flex: 1;
`
const ClaimButton = styled(Button)`
  width: 64px;
  height: 28px;
  font-size: 16px;
  color: rgba(160, 238, 9, 1);
  padding: 0;
  border: 1px solid rgba(46, 54, 59, 1);
  border-radius: 14px;
  &:hover {
    font-weight: 600;
    border-color: rgba(160, 238, 9, 1);
  }
  &:active {
    background-color: rgba(160, 238, 9, 0.1);
  }
`

export const TokenItem = memo<{
  token: {
    symbol: string
    address: string
    chainId: number
    layerOneChainId: string
    tokenId: number
    decimals: number
  }
}>(({ token }) => {
  const { symbol, tokenId, chainId, layerOneChainId, decimals } = token
  const balances = useAtomValue(v3WithdrawalBalanceAtom)
  const { provider } = useWeb3React()
  const currentNetwork = useCurrentNetwork()
  const switchNetwork = useSwitchNetwork()

  const avai = balances[layerOneChainId]?.[String(tokenId)] ?? '0'

  const handleClaim = async () => {
    if (!currentNetwork) return

    if (currentNetwork.layerOneChainId !== layerOneChainId) {
      await switchNetwork(layerOneChainId)
      return
    }

    const iface = new Interface(withdrawalAbi)
    const fragment = iface.getFunction('withdrawPendingBalance')

    const address = await provider?.getSigner().getAddress()

    const call = iface.encodeFunctionData(fragment, [
      address,
      BigNumber.from(token.tokenId).toHexString(),
      BigNumber.from(avai).toHexString(),
    ])

    const zklinkChain = V3WithdrawalZkLinkChains.find(
      (v) => Number(v.chainId) === Number(chainId),
    )

    if (!zklinkChain) {
      toastify.error(`Chain not found on zkLink, ${chainId}`)
      return
    }

    const tx = {
      from: await provider?.getSigner().getAddress(),
      to: zklinkChain.mainContract,
      data: call,
      value: 0,
    }

    provider
      ?.getSigner()
      .sendTransaction(tx)
      .catch((e: any) => {
        console.log(e)
        toastify.error(e?.message)
      })
  }
  return (
    <Wrap>
      <TokenIcon
        src={`https://app.zkex.com/icons/${symbol.toLowerCase()}.svg`}
      />
      <TokenSymbol>{symbol}</TokenSymbol>
      <TokenAvailable>
        Available: {toFixed(formatUnits(avai, 18), decimals)}
      </TokenAvailable>
      <ClaimButton onClick={handleClaim}>
        {currentNetwork?.layerOneChainId !== layerOneChainId
          ? 'Switch'
          : 'Claim'}
      </ClaimButton>
    </Wrap>
  )
})
